import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const DIRECTION_STYLES = {
  up:   { color: '#10B981', Icon: TrendingUp   },
  down: { color: '#EF4444', Icon: TrendingDown },
  hold: { color: '#94A3B8', Icon: Minus        },
  bullish:          { color: '#10B981', Icon: TrendingUp   },
  bearish:          { color: '#EF4444', Icon: TrendingDown },
  neutral:          { color: '#94A3B8', Icon: Minus        },
};

export function DirBadge({ direction, label }) {
  const { color, Icon } = DIRECTION_STYLES[direction] || DIRECTION_STYLES.hold;
  return (
    <span
      data-testid={`dir-badge-${direction}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ background: color + '20', color }}
    >
      <Icon size={10} />
      {label}
    </span>
  );
}

/**
 * Generic pinned pill hero strip.
 *
 * Props:
 *   Icon          — lucide-react icon component
 *   accent        — hex accent color (e.g. '#FBBF24' gold)
 *   title         — e.g. "India Bullion Today"
 *   subtitle      — e.g. "As of 19 Apr 2026 · 1 USD = ₹92.58"
 *   tiles[]       — [{ label, primary, badgeDirection, badgeLabel }]  (3 or 4 cells)
 *   sourceNote    — footnote
 *   testId
 */
export default function PillHero({
  Icon, accent = '#FBBF24', title, subtitle, tiles = [], sourceNote, testId = 'pill-hero',
}) {
  const cols = tiles.length === 4 ? 'grid-cols-4' : 'grid-cols-3';
  const bgFade = accent + '2E'; // ~18% alpha
  const bgMid = accent + '14';

  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="mb-3 rounded-2xl overflow-hidden relative"
      style={{
        background: `linear-gradient(135deg, ${bgFade} 0%, ${bgMid} 45%, rgba(17,17,17,1) 100%)`,
        border: `1px solid ${accent}40`,
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(120% 60% at 0% 0%, ${accent}24, transparent 60%)` }}
      />

      <div className="relative p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: accent + '2E' }}
          >
            {Icon && <Icon size={16} style={{ color: accent }} />}
          </div>
          <div className="leading-tight">
            <p
              className="text-[11px] tracking-[0.18em] uppercase font-bold"
              style={{ color: accent, fontFamily: 'Unbounded, sans-serif' }}
            >
              {title}
            </p>
            {subtitle && <p className="text-[10px] text-gray-400">{subtitle}</p>}
          </div>
        </div>

        <div className={`grid ${cols} gap-2`}>
          {tiles.map((tile, i) => (
            <div
              key={`${tile.label}-${i}`}
              data-testid={`${testId}-tile-${i}`}
              className="rounded-xl p-2.5"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <p className="text-[10px] text-gray-500 font-semibold tracking-wide">{tile.label}</p>
              <p className="text-base font-black text-white mt-0.5 leading-none">
                {tile.primary}
              </p>
              {tile.badgeDirection && (
                <div className="mt-1.5">
                  <DirBadge direction={tile.badgeDirection} label={tile.badgeLabel} />
                </div>
              )}
            </div>
          ))}
        </div>

        {sourceNote && (
          <p className="text-[9px] text-gray-600 leading-snug">{sourceNote}</p>
        )}
      </div>
    </motion.div>
  );
}
